import React, { useState, useMemo, useRef, useEffect } from "react";
import { pgAdmissionApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./PGAdmission.scss";

const COURSES = ["M.A.", "M.Sc.", "M.Com.", "M.B.A.", "M.C.A.", "M.Sc. (Physics)", "M.Sc. (Chemistry)", "M.Sc. (Mathematics)"];
const CATEGORIES = ["General", "OBC", "SC", "ST", "EWS", "Other"];
const GENDERS = ["Male", "Female", "Other"];
const ENTRANCE_EXAMS = ["None", "CUET-PG", "University Entrance Test", "Other"];
const QUOTA_TYPES = ["Open Merit", "NCC", "Sports", "Freedom Fighter", "Differently Abled", "Other"];

const STATUS_META = {
  "Applied": { label: "Applied", color: "#0ea5e9", bg: "#f0f9ff", border: "#bae6fd" },
  "Under Review": { label: "Under Review", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
  "Merit Listed": { label: "Merit Listed", color: "#8b5cf6", bg: "#f5f3ff", border: "#ddd6fe" },
  "Admitted": { label: "Admitted", color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0" },
  "Waitlisted": { label: "Waitlisted", color: "#f97316", bg: "#fff7ed", border: "#fed7aa" },
  "Rejected": { label: "Rejected", color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
};

const DOC_MAPPING = {
  "UG Degree Certificate": "UG_Degree_Certificate",
  "UG All Marksheets": "UG_Marksheets_All_Semesters",
  "Provisional Certificate": "Provisional_Certificate",
  "Transfer Certificate": "Transfer_Certificate",
  "Migration Certificate": "Migration_Certificate",
  "Category/Caste Certificate": "Category_Caste_Certificate",
  "Income Certificate": "Income_Certificate",
  "Character Certificate": "Character_Certificate",
  "Passport Photo": "Passport_Photo",
  "Aadhaar Card": "Aadhaar_Card",
  "Entrance Score Card": "Entrance_Score_Card",
  "Experience Letter": "Work_Experience_Letter",
};

const DOC_LIST = Object.keys(DOC_MAPPING);

const defaultForm = {
  _id: null,
  Application_No: "",
  Date_of_Application: "",
  First_Name: "",
  Last_Name: "",
  Father_Name: "",
  Mother_Name: "",
  Date_of_Birth: "",
  Gender: "",
  Category: "",
  Religion: "",
  Email: "",
  Phone: "",
  Residential_Address: "",
  Course_Applied_For: "",
  Specialization: "",
  Admission_Quota: "Open Merit",
  UG_Degree: "",
  UG_Percentage: "",
  UG_Institution: "",
  University_Board: "",
  UG_Passing_Year: "",
  Work_Experience: "",
  Entrance_Exam: "None",
  Entrance_Score: "",
  Documents: {
    UG_Degree_Certificate: false,
    UG_Marksheets_All_Semesters: false,
    Provisional_Certificate: false,
    Transfer_Certificate: false,
    Migration_Certificate: false,
    Category_Caste_Certificate: false,
    Income_Certificate: false,
    Character_Certificate: false,
    Passport_Photo: false,
    Aadhaar_Card: false,
    Entrance_Score_Card: false,
    Work_Experience_Letter: false,
  },
  Admission_Status: "Applied",
  Fee_Payment_Status: "Not Applicable",
  Merit_Rank: "",
  Internal_Remarks: "",
};

const TABS = ["personal", "academic", "ug-details", "documents", "status"];
const toDateStr = (d) => {
  if (!d) return "";
  const date = new Date(d);
  return date.toISOString().split('T')[0];
};

const formatDisplayDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const PGAdmission = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [formData, setFormData] = useState(defaultForm);
  const [selectedItem, setSelectedItem] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmAction, setConfirmAction] = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("personal");
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await apiClient(pgAdmissionApi);
      const result = await response.json();
      if (response.ok) {
        setApplications(result.data || []);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, []);

  const filtered = useMemo(() => {
    return applications.filter((a) => {
      const fullName = `${a.First_Name} ${a.Last_Name}`.toLowerCase();
      const matchSearch = fullName.includes(searchText.toLowerCase()) ||
        (a.Application_No || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (a.Course_Applied_For || "").toLowerCase().includes(searchText.toLowerCase());
      const matchStatus = filterStatus === "all" || a.Admission_Status === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [applications, searchText, filterStatus]);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) {
      setDropdownOpenId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 210 });
    setDropdownOpenId(id);
  };

  const openAddModal = () => {
    setFormData({
      ...defaultForm,
      Application_No: `PGA-${new Date().getFullYear()}-${Date.now().toString().slice(-4)}`,
      Date_of_Application: new Date().toISOString().slice(0, 10)
    });
    setActiveTab("personal");
    setModalType("add");
  };

  const openViewModal = (a) => { setSelectedItem(a); setModalType("view"); };

  const openEditModal = (a) => {
    setFormData({
      ...a,
      Date_of_Birth: toDateStr(a.Date_of_Birth),
      Date_of_Application: toDateStr(a.Date_of_Application)
    });
    setActiveTab("personal");
    setModalType("edit");
  };

  const closeModal = () => { 
    setModalType(""); 
    setSelectedItem(null); 
    setFormData(defaultForm); 
    setDropdownOpenId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleDocToggle = (label) => {
    const field = DOC_MAPPING[label];
    setFormData((p) => ({
      ...p,
      Documents: {
        ...p.Documents,
        [field]: !p.Documents[field]
      }
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? pgAdmissionApi : `${pgAdmissionApi}/${formData._id}`;
      const response = await apiClient(url, {
        method,
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        fetchApplications();
        closeModal();
      } else {
        const errData = await response.json();
        alert(`Error: ${errData.message || "Failed to save application"}`);
      }
    } catch (error) {
      console.error("Error saving application:", error);
      alert("Network error: Could not connect to server");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await apiClient(`${pgAdmissionApi}/${id}`, {
        method: "DELETE"
      });
      if (response.ok) {
        fetchApplications();
        setConfirmId(null);
      }
    } catch (error) {
      console.error("Error deleting application:", error);
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (id, action) => { setDropdownOpenId(null); setConfirmId(id); setConfirmAction(action); };

  const handleApprove = async (app) => {
    try {
      setLoading(true);
      const updatedApp = { ...app, Admission_Status: 'Admitted' };
      const response = await apiClient(`${pgAdmissionApi}/${app._id}`, {
        method: "PUT",
        body: JSON.stringify(updatedApp)
      });
      if (response.ok) {
        fetchApplications();
        if (modalType === "view") closeModal();
      } else {
        const errData = await response.json();
        alert(`Error: ${errData.message || "Failed to approve application"}`);
      }
    } catch (error) {
      console.error("Error approving application:", error);
      alert("Network error: Could not connect to server");
    } finally {
      setLoading(false);
    }
  };

  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const openRejectModal = (app) => {
    setDropdownOpenId(null);
    setRejectId(app._id);
    setRejectReason(app.Internal_Remarks || "");
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("Please provide a reason for rejection.");
      return;
    }
    try {
      setLoading(true);
      const app = applications.find(a => a._id === rejectId);
      const updatedApp = { ...app, Admission_Status: 'Rejected', Internal_Remarks: rejectReason };
      const response = await apiClient(`${pgAdmissionApi}/${rejectId}`, {
        method: "PUT",
        body: JSON.stringify(updatedApp)
      });
      if (response.ok) {
        fetchApplications();
        setRejectId(null);
        setRejectReason("");
        if (modalType === "view") closeModal();
      } else {
        const errData = await response.json();
        alert(`Error: ${errData.message || "Failed to reject application"}`);
      }
    } catch (error) {
      console.error("Error rejecting application:", error);
      alert("Network error: Could not connect to server");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pga-page">
      <div className="pga-header">
        <div>
          <h1>PG Admission</h1>
          <p>Manage postgraduate admission applications.</p>
        </div>
        <button type="button" className="pga-add-btn" onClick={openAddModal}>Add Application</button>
      </div>

      <div className="pga-toolbar">
        <div className="toolbar-search">
          <input type="text" placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        </div>
        <div className="toolbar-right">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            {Object.keys(STATUS_META).map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
      </div>

      <div className="pga-table-wrap">
        {loading && <div className="loading-overlay">Loading...</div>}
        <table className="pga-table">
          <thead>
            <tr>
              <th>App No.</th>
              <th>Name</th>
              <th>Course</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((app) => (
              <tr key={app._id}>
                <td>{app.Application_No}</td>
                <td>{app.First_Name} {app.Last_Name}</td>
                <td>{app.Course_Applied_For}</td>
                <td>
                  <span className="status-badge" style={{ ...STATUS_META[app.Admission_Status] }}>
                    {app.Admission_Status}
                  </span>
                </td>
                <td><button onClick={(e) => handleDotsClick(e, app._id)}>⋮</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dropdownOpenId && (
        <div ref={dropdownRef} className="action-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}>
          <button onClick={() => { setDropdownOpenId(null); openViewModal(applications.find(a => a._id === dropdownOpenId)); }}>View</button>
          <button onClick={() => { setDropdownOpenId(null); openEditModal(applications.find(a => a._id === dropdownOpenId)); }}>Edit</button>
          <button onClick={() => { setDropdownOpenId(null); handleApprove(applications.find(a => a._id === dropdownOpenId)); }} style={{ color: "#22c55e" }}>Approve</button>
          <button onClick={() => { setDropdownOpenId(null); openRejectModal(applications.find(a => a._id === dropdownOpenId)); }} style={{ color: "#ef4444" }}>Reject</button>
          <button onClick={() => openConfirm(dropdownOpenId, "delete")} className="delete">Delete</button>
        </div>
      )}

      {rejectId && (
        <div className="modal-overlay" onClick={() => setRejectId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reject Application</h3>
            <p>Please provide a reason for rejection. This will be sent to the applicant via email.</p>
            <textarea 
              value={rejectReason} 
              onChange={(e) => setRejectReason(e.target.value)} 
              rows="3" 
              style={{ width: "100%", marginTop: "10px", padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }} 
              placeholder="Rejection reason..." 
            />
            <div className="confirm-actions" style={{ marginTop: "15px" }}>
               <button onClick={() => setRejectId(null)}>Cancel</button>
               <button onClick={handleReject} className="btn-danger">Confirm Reject</button>
            </div>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this application?</p>
            <div className="confirm-actions">
              <button onClick={() => setConfirmId(null)}>Cancel</button>
              <button onClick={() => handleDelete(confirmId)} className="btn-danger">Delete</button>
            </div>
          </div>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="pga-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">📋</div>
                <div>
                  <h2>{modalType === "add" ? "New PG Application" : "Edit PG Application"}</h2>
                  <span>{formData.First_Name ? `${formData.First_Name} ${formData.Last_Name}` : "Fill in application details"}</span>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-tabs">
              {TABS.map(t => <button key={t} type="button" className={`modal-tab ${activeTab === t ? "active" : ""}`} onClick={() => setActiveTab(t)}>
                {t === "personal" ? "👤 Personal" : t === "academic" ? "🎓 Academic" : t === "ug-details" ? "🏅 UG Details" : t === "documents" ? "📄 Documents" : "⚙️ Status"}
              </button>)}
            </div>
            <form onSubmit={handleSave} className="modal-form">
              {activeTab === "personal" && (
                <div className="tab-content">
                  <div className="form-row">
                    <div className="form-group"><label>First Name *</label><input name="First_Name" value={formData.First_Name} onChange={handleChange} required /></div>
                    <div className="form-group"><label>Last Name *</label><input name="Last_Name" value={formData.Last_Name} onChange={handleChange} required /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Father's Name *</label><input name="Father_Name" value={formData.Father_Name} onChange={handleChange} required /></div>
                    <div className="form-group"><label>Mother's Name</label><input name="Mother_Name" value={formData.Mother_Name} onChange={handleChange} /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>DOB *</label><input type="date" name="Date_of_Birth" value={formData.Date_of_Birth} onChange={handleChange} required /></div>
                    <div className="form-group">
                      <label>Gender *</label>
                      <select name="Gender" value={formData.Gender} onChange={handleChange} required>
                        <option value="">Select</option>
                        {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Category *</label>
                      <select name="Category" value={formData.Category} onChange={handleChange} required>
                        <option value="">Select</option>
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="form-group"><label>Religion</label><input name="Religion" value={formData.Religion} onChange={handleChange} /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Email *</label><input type="email" name="Email" value={formData.Email} onChange={handleChange} required /></div>
                    <div className="form-group"><label>Phone *</label><input name="Phone" value={formData.Phone} onChange={handleChange} required /></div>
                  </div>
                  <div className="form-group"><label>Residential Address</label><textarea name="Residential_Address" value={formData.Residential_Address} onChange={handleChange} rows="2" /></div>
                </div>
              )}
              {activeTab === "academic" && (
                <div className="tab-content">
                  <div className="form-group">
                    <label>Course *</label>
                    <select name="Course_Applied_For" value={formData.Course_Applied_For} onChange={handleChange} required>
                      <option value="">Select</option>
                      {COURSES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Specialization</label>
                    <input name="Specialization" value={formData.Specialization} onChange={handleChange} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Entrance Exam</label>
                      <select name="Entrance_Exam" value={formData.Entrance_Exam} onChange={handleChange}>
                        {ENTRANCE_EXAMS.map(e => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </div>
                    <div className="form-group"><label>Score</label><input name="Entrance_Score" value={formData.Entrance_Score} onChange={handleChange} /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Admission Quota</label>
                      <select name="Admission_Quota" value={formData.Admission_Quota} onChange={handleChange}>
                        {QUOTA_TYPES.map(q => <option key={q} value={q}>{q}</option>)}
                      </select>
                    </div>
                    <div className="form-group"><label>Merit Rank</label><input type="number" name="Merit_Rank" value={formData.Merit_Rank} onChange={handleChange} /></div>
                  </div>
                </div>
              )}
              {activeTab === "ug-details" && (
                <div className="tab-content">
                  <div className="form-group"><label>UG Degree *</label><input name="UG_Degree" value={formData.UG_Degree} onChange={handleChange} required /></div>
                  <div className="form-row">
                    <div className="form-group"><label>UG % *</label><input name="UG_Percentage" type="number" value={formData.UG_Percentage} onChange={handleChange} required /></div>
                    <div className="form-group"><label>Passing Year</label><input name="UG_Passing_Year" value={formData.UG_Passing_Year} onChange={handleChange} /></div>
                  </div>
                  <div className="form-group"><label>Institution *</label><input name="UG_Institution" value={formData.UG_Institution} onChange={handleChange} required /></div>
                  <div className="form-group"><label>University / Board</label><input name="University_Board" value={formData.University_Board} onChange={handleChange} /></div>
                  <div className="form-group"><label>Work Experience</label><input name="Work_Experience" value={formData.Work_Experience} onChange={handleChange} placeholder="e.g. 1 year, 6 months" /></div>
                </div>
              )}
              {activeTab === "documents" && (
                <div className="tab-content">
                  <div className="doc-checklist">
                    {DOC_LIST.map(label => (
                      <label key={label} className="doc-item">
                        <input type="checkbox" checked={formData.Documents[DOC_MAPPING[label]]} onChange={() => handleDocToggle(label)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {activeTab === "status" && (
                <div className="tab-content">
                  <div className="form-group">
                    <label>Status</label>
                    <select name="Admission_Status" value={formData.Admission_Status} onChange={handleChange}>
                      {Object.keys(STATUS_META).map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Internal Remarks</label>
                    <textarea name="Internal_Remarks" value={formData.Internal_Remarks} onChange={handleChange} rows="3" />
                  </div>
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? "Saving..." : modalType === "add" ? "Submit Application" : "Save Changes"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {modalType === "view" && selectedItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="pga-modal view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">👤</div>
                <div>
                  <h2>Application Details</h2>
                  <span>{selectedItem.Application_No} • {selectedItem.First_Name} {selectedItem.Last_Name}</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <span className="status-badge" style={{ ...STATUS_META[selectedItem.Admission_Status] }}>
                  {selectedItem.Admission_Status}
                </span>
                <button type="button" className="modal-close" onClick={closeModal}>×</button>
              </div>
            </div>

            <div className="view-body">
              <div className="view-course-hero">
                <div>
                  <div className="vch-label">Course Applied</div>
                  <div className="vch-course">{selectedItem.Course_Applied_For}</div>
                  <div className="vch-meta">
                    <span>Specialization: {selectedItem.Specialization || "General"}</span>
                    <span>•</span>
                    <span>Quota: {selectedItem.Admission_Quota}</span>
                  </div>
                </div>
                <div className="vch-scores">
                  <div className="score-box accent">
                    <strong>{selectedItem.UG_Percentage}%</strong>
                    <span>UG Score</span>
                  </div>
                </div>
              </div>

              <div className="view-section">
                <h3 className="view-section-label">👤 Personal Information</h3>
                <div className="view-info-grid">
                  <div className="view-info-item"><span>Full Name</span><strong>{selectedItem.First_Name} {selectedItem.Last_Name}</strong></div>
                  <div className="view-info-item"><span>Father's Name</span><strong>{selectedItem.Father_Name}</strong></div>
                  <div className="view-info-item"><span>Mother's Name</span><strong>{selectedItem.Mother_Name || "—"}</strong></div>
                  <div className="view-info-item"><span>DOB</span><strong>{formatDisplayDate(selectedItem.Date_of_Birth)}</strong></div>
                  <div className="view-info-item"><span>Gender</span><strong>{selectedItem.Gender}</strong></div>
                  <div className="view-info-item"><span>Category</span><strong>{selectedItem.Category}</strong></div>
                  <div className="view-info-item"><span>Religion</span><strong>{selectedItem.Religion || "—"}</strong></div>
                  <div className="view-info-item"><span>Email</span><strong>{selectedItem.Email}</strong></div>
                  <div className="view-info-item"><span>Phone</span><strong>{selectedItem.Phone}</strong></div>
                  <div className="view-info-item full-span"><span>Residential Address</span><strong>{selectedItem.Residential_Address || "—"}</strong></div>
                </div>
              </div>

              <div className="view-section">
                <h3 className="view-section-label">🎓 UG Qualification Details</h3>
                <div className="view-info-grid">
                  <div className="view-info-item"><span>UG Degree</span><strong>{selectedItem.UG_Degree}</strong></div>
                  <div className="view-info-item"><span>UG Institution</span><strong>{selectedItem.UG_Institution}</strong></div>
                  <div className="view-info-item"><span>University/Board</span><strong>{selectedItem.University_Board || "—"}</strong></div>
                  <div className="view-info-item"><span>Passing Year</span><strong>{selectedItem.UG_Passing_Year || "—"}</strong></div>
                  <div className="view-info-item"><span>Work Experience</span><strong>{selectedItem.Work_Experience || "No"}</strong></div>
                  <div className="view-info-item"><span>Merit Rank</span><strong>{selectedItem.Merit_Rank || "—"}</strong></div>
                </div>
              </div>

              <div className="view-section">
                <h3 className="view-section-label">📝 Entrance Examination</h3>
                <div className="view-info-grid">
                  <div className="view-info-item"><span>Entrance Exam</span><strong>{selectedItem.Entrance_Exam}</strong></div>
                  <div className="view-info-item"><span>Entrance Score</span><strong>{selectedItem.Entrance_Score || "—"}</strong></div>
                </div>
              </div>

              <div className="view-section">
                <h3 className="view-section-label">📄 Documents Checklist</h3>
                <div className="view-doc-grid">
                  {DOC_LIST.map(label => {
                    const isSubmitted = selectedItem.Documents && selectedItem.Documents[DOC_MAPPING[label]];
                    return (
                      <div key={label} className={`view-doc-item ${isSubmitted ? "submitted" : "missing"}`}>
                        <div className="doc-icon">{isSubmitted ? "✅" : "❌"}</div>
                        <span>{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {selectedItem.Internal_Remarks && (
                <div className="view-section">
                  <h3 className="view-section-label">⚙️ Internal Remarks</h3>
                  <div className="view-remarks-box">{selectedItem.Internal_Remarks}</div>
                </div>
              )}

              <div className="view-footer-actions">
                <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
                <button type="button" className="btn" style={{ backgroundColor: "#22c55e", color: "white" }} onClick={() => handleApprove(selectedItem)}>Approve</button>
                <button type="button" className="btn" style={{ backgroundColor: "#ef4444", color: "white" }} onClick={() => openRejectModal(selectedItem)}>Reject</button>
                <button type="button" className="btn btn-primary" onClick={() => { closeModal(); openEditModal(selectedItem); }}>Edit Application</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PGAdmission;